import React, { useLayoutEffect, useRef, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTranslation } from '../useTranslation';
import { getAppLanguage } from '../utils/language';

type ThemeToggleProps = {
    theme: 'light' | 'dark';
    setTheme: (theme: 'light' | 'dark') => void;
    language?: string;
    className?: string;
    compact?: boolean;
};

/** Segmented Sun/Moon with a sliding active pill. */
const ThemeToggle: React.FC<ThemeToggleProps> = ({
    theme,
    setTheme,
    language,
    className = '',
    compact = false,
}) => {
    const { t } = useTranslation(language || getAppLanguage());
    const isDark = theme === 'dark';
    const trackRef = useRef<HTMLDivElement>(null);
    const lightRef = useRef<HTMLButtonElement>(null);
    const darkRef = useRef<HTMLButtonElement>(null);
    const [pill, setPill] = useState({ left: 0, width: 0, ready: false });

    useLayoutEffect(() => {
        const track = trackRef.current;
        const active = isDark ? darkRef.current : lightRef.current;
        if (!track || !active) return;

        const measure = () => {
            const tRect = track.getBoundingClientRect();
            const aRect = active.getBoundingClientRect();
            setPill({
                left: aRect.left - tRect.left,
                width: aRect.width,
                ready: true,
            });
        };

        measure();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        ro?.observe(track);
        window.addEventListener('resize', measure);
        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [isDark, compact, language]);

    const optionClass = (active: boolean) =>
        `relative z-10 inline-flex items-center justify-center gap-1 px-2.5 py-1 text-xs sm:text-sm font-semibold rounded-lg transition-colors duration-300 ${
            active
                ? 'text-white'
                : 'text-stone-600 dark:text-slate-300 hover:text-stone-900 dark:hover:text-white'
        }`;

    return (
        <div
            ref={trackRef}
            className={`relative inline-flex items-center gap-0.5 rounded-lg border border-stone-200/80 dark:border-slate-600/80 bg-stone-50/90 dark:bg-slate-800/90 p-0.5 ${className}`}
            role="group"
            aria-label={t.theme}
        >
            <span
                aria-hidden
                className="pointer-events-none absolute top-0.5 bottom-0.5 rounded-lg bg-blue-600 shadow-sm"
                style={{
                    left: pill.left,
                    width: pill.width,
                    opacity: pill.ready ? 1 : 0,
                    transition: pill.ready
                        ? 'left 320ms cubic-bezier(0.22, 1, 0.36, 1), width 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease'
                        : 'none',
                }}
            />
            <button
                ref={lightRef}
                type="button"
                className={optionClass(theme === 'light')}
                onClick={() => setTheme('light')}
                aria-pressed={theme === 'light'}
                title={t.lightTheme}
            >
                <Sun className="h-3.5 w-3.5 shrink-0" />
                {!compact && <span>{t.lightTheme}</span>}
            </button>
            <button
                ref={darkRef}
                type="button"
                className={optionClass(theme === 'dark')}
                onClick={() => setTheme('dark')}
                aria-pressed={theme === 'dark'}
                title={t.darkTheme}
            >
                <Moon className="h-3.5 w-3.5 shrink-0" />
                {!compact && <span>{t.darkTheme}</span>}
            </button>
        </div>
    );
};

export default React.memo(ThemeToggle);
